import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Breadcrumb from '../../components/layout/Breadcrumb'
import { SUBJECT_META } from '../../data/standards'
import { useStandards } from '../../providers/StandardsProvider'
import { useActiveExperiment } from '../../providers/ActiveExperimentProvider'
import { useAuth } from '../../providers/AuthProvider'
import { KnowledgeUnit, PASS_META, PassId, Provider, ROLE_CAPS } from '../../types'
import { useKUs } from '../../hooks/useKUs'
import { useRuns } from '../../hooks/useRuns'
import { usePrompts } from '../../hooks/usePrompts'
import PassCard from './PassCard'

export default function StandardDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const caps = ROLE_CAPS[profile?.role || 'teacher']

  const { findStandard } = useStandards()
  const { activeExperiment } = useActiveExperiment()
  const stdMeta = id ? findStandard(id) : null

  // If an experiment is active, back navigates to that experiment (preserves
  // research context). Otherwise falls back to the standards browser.
  const backLabel = activeExperiment ? activeExperiment.name : 'Standards'
  const backPath = activeExperiment ? `/datasets/${activeExperiment.id}` : '/standards'
  const { fetchKUs, upsertVote } = useKUs()
  const { executeRun } = useRuns(user?.id || '')
  const { getPrompt } = usePrompts()

  const [kusByPass, setKosByPass] = useState<Record<PassId, KnowledgeUnit[]>>({
    pass1: [],
    pass2: [],
    pass3: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadKUs = useCallback(async () => {
    if (!id) return
    setLoading(true)
    const all = await fetchKUs(id)
    setKosByPass({
      pass1: all.filter((k) => k.pass_id === 'pass1'),
      pass2: all.filter((k) => k.pass_id === 'pass2'),
      pass3: all.filter((k) => k.pass_id === 'pass3'),
    })
    setLoading(false)
  }, [id, fetchKUs])

  useEffect(() => {
    loadKUs()
  }, [loadKUs])

  const handleGenerate = useCallback(
    async (passId: PassId, provider: Provider, model: string) => {
      if (!id || !stdMeta) return
      setError(null)

      const pass = PASS_META.find((p) => p.id === passId)!
      let sourceDoc: string | undefined

      // Step 1: generate source doc for pass2/pass3
      if (pass.hasGenerate) {
        const genPrompt = getPrompt(passId, 'generate')
        if (!genPrompt) {
          setError('Generate prompt not found')
          return
        }
        const genResult = await executeRun({
          standardId: id,
          standardText: stdMeta.std.text,
          subject: stdMeta.subject,
          grade: stdMeta.grade,
          passId,
          provider,
          model,
          promptText: genPrompt.text,
          slot: 'generate',
          promptLabel: genPrompt.label,
          kuCount: 5,
        })
        if (!genResult) {
          setError('Document generation failed')
          return
        }
        // source doc comes back in the run's source_doc field via Edge Function
        sourceDoc = genResult.run.source_doc || undefined
      }

      // Step 2: extract KUs
      const extractPrompt = getPrompt(passId, 'extract')
      if (!extractPrompt) {
        setError('Extract prompt not found')
        return
      }
      const result = await executeRun({
        standardId: id,
        standardText: stdMeta.std.text,
        subject: stdMeta.subject,
        grade: stdMeta.grade,
        passId,
        provider,
        model,
        promptText: extractPrompt.text,
        slot: 'extract',
        promptLabel: extractPrompt.label,
        sourceDoc,
        kuCount: 5,
      })

      if (result) await loadKUs()
      else setError('KU extraction failed')
    },
    [id, stdMeta, getPrompt, executeRun, loadKUs],
  )

  const handleVote = useCallback(
    async (koId: string, score: number) => {
      if (!user) return
      await upsertVote(koId, user.id, score, '')
      await loadKUs()
    },
    [user, upsertVote, loadKUs],
  )

  const handleOpenKU = useCallback(
    (ku: KnowledgeUnit) => {
      navigate(`/standard/${id}/ku/${ku.id}`)
    },
    [navigate, id],
  )

  if (!stdMeta) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Breadcrumb label={backLabel} onBack={() => navigate(backPath)} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Standard not found</Alert>
        </Box>
      </Box>
    )
  }

  const subjectMeta = SUBJECT_META[stdMeta.subject]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Breadcrumb label="Standards" onBack={() => navigate('/standards')} />

      {/* Standard header */}
      <Box
        sx={{ px: 2, pt: 2, pb: 1.5, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Chip
            label={id}
            size="small"
            sx={{ bgcolor: subjectMeta.bg, color: subjectMeta.color, fontWeight: 800, fontSize: 12 }}
          />
          <Typography variant="caption" sx={{ color: subjectMeta.color, fontWeight: 700, fontSize: 11 }}>
            {stdMeta.subject} · Grade {stdMeta.grade}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6, fontSize: 13 }}>
          {stdMeta.std.text}
        </Typography>
        {stdMeta.domain && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block', mt: 0.5 }}>
            {stdMeta.domain}
            {stdMeta.cluster ? ` › ${stdMeta.cluster}` : ''}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Pass cards */}
      <Box sx={{ flex: 1, p: 2, pb: 4, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          PASS_META.map((pass) => (
            <PassCard
              key={pass.id}
              pass={pass}
              standardId={id!}
              standardText={stdMeta.std.text}
              subject={stdMeta.subject}
              grade={stdMeta.grade}
              kus={kusByPass[pass.id]}
              extractPrompt={getPrompt(pass.id, 'extract')}
              generatePrompt={pass.hasGenerate ? getPrompt(pass.id, 'generate') : undefined}
              canGenerate={caps.canGenerate}
              onGenerate={(provider, model) => handleGenerate(pass.id, provider, model)}
              onOpenKU={handleOpenKU}
              userId={user?.id || ''}
              onVote={handleVote}
            />
          ))
        )}
      </Box>
    </Box>
  )
}
